import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const AcceptInvite: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!name.trim() || !password || !confirmPassword) {
      setError(t("acceptInvite.fillAllFields"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("acceptInvite.passwordsNoMatch"));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/users/accept-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, name, password }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || t("acceptInvite.failedToAccept"));
      } else {
        setSuccess(t("acceptInvite.accountCreated"));
        setTimeout(() => navigate("/login"), 2500);
      }
    } catch {
      setError(t("acceptInvite.failedToAcceptRetry"));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-stone-50 p-4">
      <img src="/reviewcounter-logo.png" alt="ReviewCounter" className="w-20 h-20 object-contain mb-6" />
      <div className="bg-white rounded-xl shadow p-6 w-full max-w-md">
        <h1 className="text-xl font-bold mb-4 text-center">{t("acceptInvite.title")}</h1>
        <form onSubmit={handleSubmit}>
          <label className="block mb-2 font-medium">{t("acceptInvite.yourName")}</label>
          <input
            type="text"
            className="w-full border rounded px-3 py-2 mb-4"
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={loading}
            required
          />
          <label className="block mb-2 font-medium">{t("acceptInvite.password")}</label>
          <input
            type="password"
            className="w-full border rounded px-3 py-2 mb-4"
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={loading}
            minLength={8}
            required
          />
          <label className="block mb-2 font-medium">{t("acceptInvite.confirmPassword")}</label>
          <input
            type="password"
            className="w-full border rounded px-3 py-2 mb-4"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            disabled={loading}
            minLength={8}
            required
          />
          <button
            type="submit"
            className="w-full bg-teal-600 text-white py-2 rounded font-semibold mt-2 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? t("acceptInvite.creatingAccount") : t("acceptInvite.acceptInvite")}
          </button>
        </form>
        {(success || error) && (
          <div className={`mt-4 text-center font-semibold ${success ? 'text-green-600' : 'text-red-600'}`}>{success || error}</div>
        )}
        <div className="mt-8 text-xs text-gray-400 text-center">
          {t("acceptInvite.linkValidityNote")}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite; 